import { Badge, Button, IconButton, Modal, Select } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArticleListItem, BarcodeListItem } from '@warehouse/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { listBarcodes, removeBarcode } from '../api';
import { ArticlePicker } from '../components/ArticlePicker';
import { ScanPanel } from '../components/ScanPanel';
import { useSuppliers } from '../lookups';
import { BarcodeFormModal } from './BarcodeFormModal';

interface PendingBind {
  raw: string;
  supplierId: string | null;
  mode: 'manual' | 'teach';
}

/**
 * Barcode administration (MODULES §M1: "manual add barcode↔article+supplier,
 * teach history view").
 *
 * Three things live on one page because they are one job: look up what a code
 * resolves to, see every code the system knows (including which ones a scanner
 * taught it and who), and bind a new one. The `taught` filter IS the teach
 * history view — a separate table would only drift.
 */
export function BarcodesPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });
  const canManage = usePermission('registers.barcodes.manage');
  const canTeach = usePermission('warehouse.barcodes.teach');
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { options: supplierOptions } = useSuppliers();

  const [pending, setPending] = useState<PendingBind | null>(null);
  const [article, setArticle] = useState<ArticleListItem | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['master-data', 'barcodes', listQueryKey(state)],
    queryFn: () => listBarcodes(state),
  });
  useLiveQuery(['barcodes'], ['master-data', 'barcodes']);

  const remove = useMutation({
    mutationFn: (id: string) => removeBarcode(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data', 'barcodes'] });
      toastSuccess(t('masterData:barcodes.removed'));
    },
    onError: (err: unknown) => {
      toastError(err);
    },
  });

  const columns: DataTableColumn<BarcodeListItem>[] = [
    {
      id: 'barcode',
      header: t('masterData:barcodes.columns.barcode'),
      sortField: 'barcode',
      cell: (row) => <span className="font-mono text-sm">{row.barcode}</span>,
    },
    {
      id: 'kind',
      header: t('masterData:barcodes.columns.kind'),
      sortField: 'kind',
      cell: (row) => (
        <Badge tone={row.kind === 'gtin' ? 'brand' : 'neutral'} size="sm">
          {t(`masterData:barcodes.kinds.${row.kind}`)}
        </Badge>
      ),
    },
    {
      id: 'article',
      header: t('masterData:barcodes.columns.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <Link className="text-brand-600 hover:underline" to={`/registers/articles/${row.articleId}`}>
          <span className="font-mono text-xs">{row.articleNo}</span> — {row.articleName}
        </Link>
      ),
    },
    {
      id: 'supplier',
      header: t('masterData:barcodes.columns.supplier'),
      sortField: 'supplierName',
      hideBelow: 'md',
      cell: (row) => row.supplierName ?? '—',
    },
    {
      id: 'unit',
      header: t('masterData:barcodes.columns.unit'),
      hideBelow: 'lg',
      cell: (row) => <span className="font-mono text-xs">{row.unitCode ?? '—'}</span>,
    },
    {
      id: 'qtyPerScan',
      header: t('masterData:barcodes.columns.qtyPerScan'),
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => formatQty(row.qtyPerScan),
    },
    {
      id: 'taughtBy',
      header: t('masterData:barcodes.columns.taughtBy'),
      hideBelow: 'lg',
      cell: (row) =>
        row.taughtBy ? (
          <span className="text-sm">{row.taughtByName ?? '—'}</span>
        ) : (
          <span className="text-sm text-ink-muted">{t('masterData:barcodes.manual')}</span>
        ),
    },
    {
      id: 'createdAt',
      header: t('masterData:barcodes.columns.createdAt'),
      sortField: 'createdAt',
      hideBelow: 'xl',
      cell: (row) => <span className="text-xs text-ink-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: '',
      width: 'w-16',
      align: 'right',
      cell: (row) =>
        canManage ? (
          <IconButton
            variant="danger"
            aria-label={t('common:actions.delete')}
            onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: t('masterData:barcodes.confirmRemove.title'),
                  message: t('masterData:barcodes.confirmRemove.message', { barcode: row.barcode }),
                });
                if (ok) remove.mutate(row.id);
              })();
            }}
          >
            <Trash2 className="size-4" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <>
      {dialog}
      <PageHeader
        title={t('masterData:barcodes.title')}
        subtitle={t('masterData:barcodes.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setArticle(null);
                setPending({ raw: '', supplierId: null, mode: 'manual' });
              }}
            >
              {t('masterData:barcodes.new')}
            </Button>
          ) : null
        }
      />

      <div className="mb-6">
        <ScanPanel
          onTeach={
            canTeach || canManage
              ? (raw, supplierId) => {
                  setArticle(null);
                  setPending({ raw, supplierId, mode: canTeach ? 'teach' : 'manual' });
                }
              : undefined
          }
        />
      </div>

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:barcodes.searchPlaceholder')}
        emptyTitle={t('masterData:barcodes.empty')}
        emptyDescription={t('masterData:barcodes.emptyDescription')}
        filters={
          <>
            <Select
              aria-label={t('masterData:barcodes.filters.kind')}
              className="w-44"
              options={[
                { value: '', label: t('masterData:barcodes.filters.kind') },
                { value: 'gtin', label: t('masterData:barcodes.kinds.gtin') },
                { value: 'raw', label: t('masterData:barcodes.kinds.raw') },
              ]}
              value={String(state.filters?.kind ?? '')}
              onChange={(event) => {
                setFilter('kind', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('masterData:barcodes.filters.supplier')}
              className="w-52"
              options={[{ value: '', label: t('masterData:barcodes.filters.supplier') }, ...supplierOptions]}
              value={String(state.filters?.supplierId ?? '')}
              onChange={(event) => {
                setFilter('supplierId', event.target.value || undefined);
              }}
            />
            <Select
              aria-label={t('masterData:barcodes.filters.origin')}
              className="w-52"
              options={[
                { value: '', label: t('masterData:barcodes.filters.origin') },
                { value: 'true', label: t('masterData:barcodes.filters.taught') },
                { value: 'false', label: t('masterData:barcodes.filters.manual') },
              ]}
              value={String(state.filters?.taught ?? '')}
              onChange={(event) => {
                setFilter('taught', event.target.value === '' ? undefined : event.target.value === 'true');
              }}
            />
          </>
        }
      />

      {pending && !article ? (
        <Modal
          open
          onClose={() => {
            setPending(null);
          }}
          title={pending.mode === 'teach' ? t('masterData:barcodes.teach.title') : t('masterData:barcodes.new')}
          subtitle={t('masterData:barcodes.fields.article')}
          width={560}
        >
          <ArticlePicker
            onSelect={(picked) => {
              setArticle(picked);
            }}
          />
        </Modal>
      ) : null}

      {pending && article ? (
        <BarcodeFormModal
          articleId={article.id}
          fixedArticle={`${article.articleNo} — ${article.name}`}
          raw={pending.raw || undefined}
          mode={pending.mode}
          defaultSupplierId={pending.supplierId ?? article.supplierId}
          onClose={() => {
            setPending(null);
            setArticle(null);
          }}
        />
      ) : null}
    </>
  );
}
